import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone, MousePointerClick } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { MentorCommentsDisplay, MentorInlineComment } from './MentorComponents.jsx';
import { AnimalAvatar } from './AnimalAvatars.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, detectUnconstructiveContent, containsProfanity, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function MenteeResponseCard({ mentee, response, isCurrentUser, userHasAcknowledged, currentUserId, currentUserName, conflict, onUpdate, isObserver = false }) {
  const toast = useToast();
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isResolved = conflict.status === 'resolved';

  const [commentWarning, setCommentWarning] = useState(null);

  const handleCommentChange = (text) => {
    setComment(text);
    if (text.length > 5) {
      const toneCheck = detectUnconstructiveContent(text);
      if (toneCheck.isUnconstructive) {
        setCommentWarning(toneCheck.reason);
      } else {
        const crisisCheck = detectHarmContent(text);
        setCommentWarning(crisisCheck.detected
          ? 'This message may indicate someone needs support.'
          : null);
      }
    } else {
      setCommentWarning(null);
    }
  };

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          identifyDefine: {
            ...conflict.steps.identifyDefine,
            data: {
              ...conflict.steps.identifyDefine.data,
              [mentee.id]: {
                ...response,
                acknowledgments: [
                  ...(response.acknowledgments || []),
                  {
                    userId: currentUserId,
                    userName: currentUserName,
                    timestamp: new Date().toISOString()
                  }
                ]
              }
            }
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: currentUserId
      };

      addAuditEntry(updated, currentUserId, currentUserName, 'Acknowledged paraphrase', `Acknowledged ${mentee.name}'s paraphrase`);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      onUpdate();
    } catch (error) {
      console.error('Error acknowledging:', error);
      toast('Failed to acknowledge. Please try again.');
    }
    setSubmitting(false);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    // Check for profanity
    if (containsProfanity(comment)) {
      toast('Please remove profanity from your comment');
      return;
    }

    // Check for unconstructive/nasty content
    const toneCheck = detectUnconstructiveContent(comment);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep comments respectful and constructive.');
      return;
    }

    // Check for crisis-level content (don't block, but flag)
    const crisisCheck = detectHarmContent(comment);
    
    setSubmitting(true);
    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          identifyDefine: {
            ...conflict.steps.identifyDefine,
            data: {
              ...conflict.steps.identifyDefine.data,
              [mentee.id]: {
                ...response,
                comments: [
                  ...(response.comments || []),
                  {
                    userId: currentUserId,
                    userName: currentUserName,
                    text: comment,
                    timestamp: new Date().toISOString()
                  }
                ]
              }
            }
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: currentUserId
      };

      await storage.set(`conflict:${conflict.id}`, updated, true);
      setComment('');
      setShowCommentBox(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast('Failed to add comment. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className={`p-4 rounded-lg border-2 ${
      isCurrentUser ? 'bg-blue-50 border-blue-200' : 'bg-white border-stone-200'
    }`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-8 h-8 flex-shrink-0">
            {mentee.avatar ? (
              <AnimalAvatar animal={mentee.avatar} size={32} />
            ) : (
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-medium">
                {(mentee.name || mentee.email || 'U')[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-stone-800 break-words">
              {mentee.email || mentee.name || 'Unknown user'}
              {isCurrentUser && <span className="text-xs text-blue-600 ml-2">(You)</span>}
            </div>
            <div className="text-xs text-stone-500">
              {new Date(response.timestamp).toLocaleString()}
            </div>
          </div>
        </div>
        
        {!isCurrentUser && !userHasAcknowledged && !isObserver && !isResolved && (
          <button
            onClick={handleAcknowledge}
            disabled={submitting}
            className="flex items-center justify-center gap-1.5 px-4 py-2 bg-green-700 hover:bg-green-800 disabled:bg-green-400 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-all active:scale-95 flex-shrink-0 border border-green-700 cursor-pointer"
          >
            <MousePointerClick className="w-4 h-4" />
            {submitting ? 'Acknowledging...' : 'I Acknowledge This'}
          </button>
        )}
        
        {!isCurrentUser && userHasAcknowledged && (
          <div className="flex items-center gap-1 text-green-700 text-sm flex-shrink-0">
            <CheckCircle className="w-4 h-4" />
            Acknowledged
          </div>
        )}
      </div>

      <p className="text-stone-700 mb-3 break-words">{response.paraphrase}</p>

      {/* Upset Rating */}
      {response.upsetRating && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-stone-200">
          <span className="text-xs font-medium text-stone-500">How upsetting this issue feels to them:</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(n => (
              <div key={n} className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
                n <= response.upsetRating
                  ? response.upsetRating <= 2 ? 'bg-green-500 text-white'
                    : response.upsetRating === 3 ? 'bg-amber-500 text-stone-900'
                    : 'bg-red-600 text-white'
                  : 'bg-stone-100 text-stone-600'
              }`}>{n}</div>
            ))}
          </div>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            response.upsetRating <= 2 ? 'bg-green-100 text-green-700'
              : response.upsetRating === 3 ? 'bg-amber-100 text-amber-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {response.upsetRating <= 2 ? 'Mildly upsetting' : response.upsetRating === 3 ? 'Moderately upsetting' : response.upsetRating === 4 ? 'Very upsetting' : 'Extremely upsetting'}
          </span>
        </div>
      )}

      {/* Acknowledgments */}
      {response.acknowledgments && response.acknowledgments.length > 0 && (
        <div className="mb-3 pb-3 border-b border-stone-200">
          <div className="text-xs text-stone-500 mb-2">
            Acknowledged by: {response.acknowledgments.map(a => a.userName).join(', ')}
          </div>
        </div>
      )}

      {/* Comments */}
      {response.comments && response.comments.length > 0 && (
        <div className="space-y-2 mb-3">
          {response.comments.map((c, idx) => (
            <div key={idx} className="bg-stone-50 p-3 rounded-lg">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-stone-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-stone-600 mb-1">
                    {c.userName} · {new Date(c.timestamp).toLocaleTimeString()}
                  </div>
                  <p className="text-sm text-stone-700">{c.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Comment */}
      {!isCurrentUser && !isResolved && (
        <div>
          {!showCommentBox ? (
            <button
              onClick={() => setShowCommentBox(true)}
              className="text-sm text-amber-700 hover:text-amber-800 font-medium"
            >
              + Add clarification or comment
            </button>
          ) : (
            <div className="space-y-2">
              <textarea
                value={comment}
                onChange={(e) => handleCommentChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                  commentWarning 
                    ? 'border-red-300 focus:ring-red-400 bg-red-50' 
                    : 'border-stone-300 focus:ring-amber-500'
                }`}
                placeholder="Ask for clarification or add a comment..."
                rows={2}
                maxLength={500}
              />
              {commentWarning && (
                <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                  <ShieldAlert className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{commentWarning}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleAddComment}
                  disabled={submitting || !comment.trim()}
                  className="px-4 py-1 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 text-white text-sm rounded-lg transition-colors"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
                <button
                  onClick={() => {
                    setShowCommentBox(false);
                    setComment('');
                  }}
                  className="px-4 py-1 bg-stone-200 hover:bg-stone-300 text-stone-700 text-sm rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mentor inline comment on this paraphrase */}
      <MentorInlineComment entryId={`id-paraphrase-${mentee.id}`} entryType="paraphrase" conflict={conflict} user={{id: currentUserId, name: currentUserName}} onUpdate={onUpdate} />
      <MentorCommentsDisplay entryId={`id-paraphrase-${mentee.id}`} conflict={conflict} user={{id: currentUserId, name: currentUserName}} />
    </div>
  );
}

// Identify & Define Step

export default MenteeResponseCard;
